import { Box, TextFieldProps, Typography, useTheme } from '@mui/material';
import { useField } from 'formik';
import { isEmpty, startsWith } from 'lodash';
import React, { useCallback } from 'react';
import PhoneInput, { CountryData, PhoneInputProps } from 'react-phone-input-2';
import ru from 'react-phone-input-2/lang/ru.json';

import { useFormikError } from 'hooks';

const LEFT_MARGIN = 3.5;

type Props = {
  name: string;
  label: string;
  localization?: PhoneInputProps['localization'];
} & Partial<PhoneInputProps> &
  Partial<TextFieldProps>;

export const FormikPhoneInput: React.FC<Props> = ({
  name,
  label,
  localization,
  country = 'ru',
  ...rest
}) => {
  const theme = useTheme();

  const [field, , helpers] = useField(name);

  const { errorVisible, errorMessage } = useFormikError(name);

  const handleChange = useCallback(
    (
      phoneNumber: string,
      data: {} | CountryData,
      event: React.ChangeEvent<HTMLInputElement>,
      formattedValue: string,
    ) => {
      const numberWithPlus =
        isEmpty(phoneNumber) || startsWith(phoneNumber, '+')
          ? phoneNumber
          : `+${phoneNumber}`;
      helpers.setValue(numberWithPlus);
    },
    [helpers],
  );

  return (
    <Box
      sx={
        theme.palette.mode === 'dark'
          ? {
              // Keep the library defaults in light mode; override light-only colors in dark mode.
              '& .react-tel-input': {
                // input and flag share one border color, so the overlapped left edge matches
                '.form-control, .flag-dropdown': {
                  borderColor: 'divider',
                },
                '&:hover .form-control:not([disabled]):not(:focus), &:hover .form-control:not([disabled]):not(:focus) + .flag-dropdown':
                  {
                    borderColor: 'text.primary',
                  },
                '.form-control:focus, .form-control:focus + .flag-dropdown': {
                  borderColor: 'primary.main',
                },
                '.flag-dropdown, .flag-dropdown.open, .flag-dropdown.open .selected-flag':
                  {
                    backgroundColor: 'transparent',
                  },
                '.selected-flag:hover, .selected-flag:focus': {
                  backgroundColor: 'action.hover',
                },
                '.selected-flag .arrow': {
                  borderTopColor: 'text.secondary',
                },
                '.selected-flag .arrow.up': {
                  borderBottomColor: 'text.secondary',
                },
                '.country-list': {
                  backgroundColor: 'background.paper',
                  color: 'text.primary',
                  borderRadius: 1,
                  boxShadow: 3,
                },
                '.country-list .country .dial-code': {
                  color: 'text.secondary',
                },
                '.country-list .country:hover, .country-list .country.highlight':
                  {
                    backgroundColor: 'action.hover',
                  },
                // not used yet (enableSearch / preferredCountries), but white by default
                '.country-list .search': {
                  backgroundColor: 'background.paper',
                },
                '.country-list .search-box': {
                  backgroundColor: 'transparent',
                  borderColor: 'divider',
                  color: 'text.primary',
                },
                '.country-list .divider': {
                  borderBottomColor: 'divider',
                },
              },
            }
          : {}
      }
    >
      <PhoneInput
        {...rest}
        inputProps={{
          label,
          name: field.name,
          required: rest.required,
          autoFocus: true,
          style: {
            ...(rest.fullWidth && { width: '100%' }),
            ...(rest.disabled && {
              color: theme.palette.action.disabled,
            }),
            background: 'transparent',
          },
        }}
        localization={ru}
        country={country}
        jumpCursorToEnd
        value={field.value}
        placeholder={label}
        disabled={rest.disabled}
        onChange={handleChange}
      />
      {errorVisible && errorMessage && (
        <Typography marginLeft={LEFT_MARGIN} variant="caption" color="error">
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};
